<template>
    <v-container fluid class="pa-0">
        <div class="book-view">
            <skeleton-loader v-if="loading" type="book-shift" />

            <template v-else>
                <span class="book-hint">{{ $t('business.book_shift_hint') }}</span>

                <v-slide-group v-model="day" class="day-group" mandatory show-arrows="desktop">
                    <v-slide-item v-for="item in days" :key="item.value" :value="item.value"
                        v-slot="{ active, toggle }">
                        <button type="button" class="day-tab" :class="{ 'day-tab--active': active }" @click="toggle">
                            <span class="day-name">
                                {{ item.name }}<template v-if="item.today"> · {{ $t('common.today') }}</template>
                            </span>
                            <span class="day-count" :class="{ 'day-count--none': !item.count }">
                                {{ item.count || '–' }}
                            </span>
                        </button>
                    </v-slide-item>
                </v-slide-group>

                <div v-if="dayShifts.length === 0" class="book-empty">{{ $t('business.no_shifts_day') }}</div>

                <div v-else class="shift-list">
                    <div v-for="shift in dayShifts" :key="shift.id" class="shift-row"
                        :class="{ 'shift-row--blocked': isBlocked(shift) }">
                        <span class="shift-dot" :style="{ background: activityColor(shift) }" />
                        <div class="shift-info">
                            <span class="shift-name">{{ activityName(shift) }}</span>
                            <span class="shift-time">{{ shift.startTime }} - {{ shift.endTime }}</span>
                            <span class="shift-seats"
                                :class="{ 'shift-seats--full': isFull(shift) && !isBlocked(shift) }">
                                {{ seatsLabel(shift) }}
                            </span>
                        </div>
                        <div class="shift-action">
                            <template v-if="isBooked(shift)">
                                <span class="shift-chip" :class="{ 'shift-chip--fixed': isFixed(shift) }">
                                    {{ isFixed(shift) ? $t('business.booking_fixed_short') : $t('business.booked') }}
                                </span>
                                <button type="button" class="shift-cancel" :disabled="saving === shift.id"
                                    @click="askLeave(shift)">
                                    {{ $t('common.cancel') }}
                                </button>
                            </template>
                            <v-btn v-else depressed small :color="disabledFor(shift) ? '' : 'primary'"
                                :class="{ 'black--text': !disabledFor(shift) }" :disabled="disabledFor(shift)"
                                :loading="saving === shift.id" @click="askBooking(shift)">
                                {{ bookLabel(shift) }}
                            </v-btn>
                        </div>
                    </div>
                </div>
            </template>
        </div>

        <booking-type-dialog ref="typeDialog" :subtitle="pendingLabel" @select="confirmBooking" />

        <kimsa-confirm ref="confirm" show-cancel alert-mode :title="$t('business.leave_fixed_shift')"
            :content="confirmText" :confirm-label="$t('business.leave')" @confirm="confirmLeave" />
    </v-container>
</template>

<script>
import SkeletonLoader from "@/components/common/SkeletonLoader";
import KimsaConfirm from "@/components/common/KimsaConfirm";
import BookingTypeDialog from "@/components/common/BookingTypeDialog";
import { capitalize } from "@/utils/Common";
import {
    ACTIVITY_PALETTE, FREQUENCY_WEEKLY, SHIFT_FULL, WEEK_DAYS, bookShift, cancelBooking, frequencyBlock, isShiftFull,
    loadClientByEmail, loadCoachCompanyId, loadSettings, loadShiftBookings, loadShifts, loadWeeksBookings, memberCount,
    monthOf, monthWeekStarts, normalizeFrequency, planAllowsShift, shiftDate, weekStartOf,
} from "@/utils/Business";

export default {
    name: "StudentBookShift",
    components: { SkeletonLoader, KimsaConfirm, BookingTypeDialog },
    data() {
        return {
            loading: true,
            saving: null,
            companyId: null,
            shifts: [],
            activities: [],
            planTypes: [],
            client: null,
            bookings: {},
            weeksBookings: {},
            weekStart: weekStartOf(),
            day: 1,
            leaving: null,
            pending: null,
        }
    },
    computed: {
        studentId(vm = this) {
            return vm.currentUser?.uid || null
        },
        coachId(vm = this) {
            return vm.account?.studentSubscription?.coachId || null
        },
        days(vm = this) {
            void vm.$i18n.locale
            const today = vm.$moment().isoWeekday()
            return WEEK_DAYS.map(value => ({
                value,
                name: vm.$moment().isoWeekday(value).format('ddd').replace('.', ''),
                today: value === today,
                count: vm.shifts.filter(shift => shift.day === value).length,
            }))
        },
        dayShifts(vm = this) {
            return vm.shifts
                .filter(shift => shift.day === vm.day)
                .sort((a, b) => a.startTime.localeCompare(b.startTime))
        },
        confirmText(vm = this) {
            if (!vm.leaving) return ''
            return vm.$t('business.leave_shift_confirm', { shift: vm.shiftLabel(vm.leaving) })
        },
        pendingLabel(vm = this) {
            if (!vm.pending) return ''
            return vm.shiftLabel(vm.pending)
        },
        planType(vm = this) {
            return vm.planTypes.find(item => item.id === vm.client?.planId) || null
        },
        planRules(vm = this) {
            return vm.planType?.activities || []
        },
        frequency(vm = this) {
            return normalizeFrequency(vm.planType?.frequency)
        },
        exceptions(vm = this) {
            return vm.client?.exceptions || []
        },
    },
    created() {
        this.day = this.$moment().isoWeekday()
    },
    mounted() {
        this.load()
    },
    methods: {
        async load() {
            const vm = this
            vm.loading = true
            try {
                vm.companyId = await loadCoachCompanyId(vm.coachId)
                if (!vm.companyId) return vm.pushRoute({ name: 'student.home' })
                const [shifts, settings, bookings, client] = await Promise.all([
                    loadShifts(vm.companyId),
                    loadSettings(vm.companyId),
                    loadShiftBookings(vm.companyId, vm.weekStart),
                    loadClientByEmail(vm.companyId, vm.account?.email),
                ])
                vm.shifts = shifts
                vm.activities = settings.activities
                vm.planTypes = settings.planTypes
                vm.bookings = bookings
                vm.client = client
                await vm.loadMonthBookings()
            } catch (error) {
                console.error('[business] loadShifts student', error?.code, error?.message)
                vm.toast(vm.$t('common.error_generic'), 'error')
            } finally {
                vm.loading = false
            }
        },
        async loadMonthBookings() {
            const vm = this
            if (!vm.frequency.monthly) return vm.weeksBookings = {}
            const months = [monthOf(vm.weekStart), monthOf(shiftDate(vm.weekStart, 7))]
            const weeks = [...new Set(months.flatMap(month => monthWeekStarts(month)))]
                .filter(weekStart => weekStart !== vm.weekStart)
            vm.weeksBookings = await loadWeeksBookings(vm.companyId, weeks)
        },
        shiftLabel(shift) {
            const vm = this
            const day = capitalize(vm.$moment().isoWeekday(shift.day).format('dddd'))
            return `${vm.activityName(shift)} · ${day} ${shift.startTime}`
        },
        activity(shift) {
            return this.activities.find(item => item.id === shift.activityId) || null
        },
        activityName(shift) {
            return this.activity(shift)?.name || this.$t('business.no_activity')
        },
        activityColor(shift) {
            return this.activity(shift)?.color || ACTIVITY_PALETTE[0]
        },
        isFixed(shift) {
            return !!shift.members[this.studentId]
        },
        isBooked(shift) {
            return this.isFixed(shift) || !!this.bookings[shift.id]?.[this.studentId]
        },
        isFull(shift) {
            return isShiftFull(shift, this.bookings)
        },
        plan(shift) {
            return planAllowsShift(shift, this.planRules, this.exceptions)
        },
        frequencyBlock(shift, fixed = false) {
            const vm = this
            return frequencyBlock({
                shift,
                weekStart: vm.weekStart,
                fixed,
                frequency: vm.frequency,
                shifts: vm.shifts,
                bookings: vm.bookings,
                weeksBookings: vm.weeksBookings,
                key: vm.studentId,
            })
        },
        frequencyMessage(block) {
            const key = block.reason === FREQUENCY_WEEKLY
                ? 'business.frequency_weekly_reached'
                : 'business.frequency_monthly_reached'
            return this.$tc(key, block.limit, { n: block.limit })
        },
        isBlocked(shift) {
            return !this.isBooked(shift) && (!this.plan(shift).allowed || !!this.frequencyBlock(shift))
        },
        disabledFor(shift) {
            return this.isBlocked(shift) || this.isFull(shift)
        },
        bookLabel(shift) {
            const vm = this
            if (vm.isBlocked(shift)) return vm.$t('business.not_available')
            if (vm.isFull(shift)) return vm.$t('business.shift_full_label')
            return vm.$t('business.book')
        },
        seatsLeft(shift) {
            return Math.max(0, (shift.capacity || 0) - memberCount(shift, this.bookings))
        },
        seatsLabel(shift) {
            const vm = this
            if (vm.isBlocked(shift)) return vm.blockedReason(shift)
            if (vm.isFull(shift)) return vm.$t('business.no_seats_left')
            return vm.$tc('business.seats_left', vm.seatsLeft(shift), { n: vm.seatsLeft(shift) })
        },
        blockedReason(shift) {
            const vm = this
            const block = vm.plan(shift).allowed && vm.frequencyBlock(shift)
            if (block) return vm.frequencyMessage(block)
            const rule = vm.plan(shift).rule
            const activity = vm.activityName(shift)
            if (!rule) {
                const plan = vm.planTypes.find(item => item.id === vm.client?.planId)
                return vm.$t('business.plan_excludes_activity', { plan: plan?.name || '', activity })
            }
            const days = rule.days
                .map(day => vm.$moment().isoWeekday(day).format('ddd').replace('.', ''))
                .join('/')
            return vm.$t('business.plan_limits_activity', {
                activity,
                days,
                from: rule.startTime,
                to: rule.endTime,
            })
        },
        askBooking(shift) {
            const vm = this
            if (vm.disabledFor(shift)) return
            vm.pending = shift
            vm.$refs.typeDialog?.show()
        },
        confirmBooking(fixed) {
            const vm = this
            const shift = vm.pending
            vm.pending = null
            if (!shift) return
            const block = vm.frequencyBlock(shift, fixed)
            if (block) return vm.toast(vm.frequencyMessage(block), 'error')
            vm.book(shift, fixed)
        },
        askLeave(shift) {
            const vm = this
            if (!vm.isFixed(shift)) return vm.leave(shift, false)
            vm.leaving = shift
            vm.$refs.confirm?.show()
        },
        confirmLeave() {
            const vm = this
            const shift = vm.leaving
            vm.leaving = null
            if (shift) vm.leave(shift, true)
        },
        storeShift(shift, members) {
            const vm = this
            const index = vm.shifts.findIndex(item => item.id === shift.id)
            if (index >= 0) vm.shifts.splice(index, 1, { ...shift, members })
        },
        storeBooking(shiftId, members) {
            this.bookings = { ...this.bookings, [shiftId]: members }
        },
        async book(shift, fixed) {
            const vm = this
            const previousMembers = shift.members
            const previousBooking = vm.bookings[shift.id] || {}
            const member = { name: vm.account?.name || '' }

            vm.saving = shift.id
            if (fixed) vm.storeShift(shift, { ...previousMembers, [vm.studentId]: member })
            else vm.storeBooking(shift.id, { ...previousBooking, [vm.studentId]: member })

            try {
                await bookShift(shift.id, vm.weekStart, vm.studentId, member.name, fixed)
            } catch (error) {
                console.error('[business] bookShift', error?.code, error?.message)
                if (fixed) vm.storeShift(shift, previousMembers)
                else vm.storeBooking(shift.id, previousBooking)
                const key = error?.message === SHIFT_FULL ? 'business.shift_taken' : 'common.error_generic'
                vm.toast(vm.$t(key), 'error')
                if (error?.message === SHIFT_FULL) vm.reload()
            } finally {
                vm.saving = null
            }
        },
        async leave(shift, fixed) {
            const vm = this
            const previousMembers = shift.members
            const previousBooking = vm.bookings[shift.id] || {}

            vm.saving = shift.id
            if (fixed) {
                const members = { ...previousMembers }
                delete members[vm.studentId]
                vm.storeShift(shift, members)
            } else {
                const members = { ...previousBooking }
                delete members[vm.studentId]
                vm.storeBooking(shift.id, members)
            }

            try {
                await cancelBooking(shift.id, vm.weekStart, vm.studentId, fixed)
            } catch (error) {
                console.error('[business] cancelBooking', error?.code, error?.message)
                if (fixed) vm.storeShift(shift, previousMembers)
                else vm.storeBooking(shift.id, previousBooking)
                vm.toast(vm.$t('common.error_generic'), 'error')
            } finally {
                vm.saving = null
            }
        },
        async reload() {
            const vm = this
            try {
                const [shifts, bookings] = await Promise.all([
                    loadShifts(vm.companyId),
                    loadShiftBookings(vm.companyId, vm.weekStart),
                ])
                vm.shifts = shifts
                vm.bookings = bookings
            } catch (error) {
                console.error('[business] loadShifts student', error?.code, error?.message)
            }
        },
    },
}
</script>

<style scoped>
.book-view {
    display: flex;
    flex-direction: column;
    gap: 14px;
    max-width: 700px;
    margin: 0 auto;
}

.book-hint {
    font-size: var(--fs-caption);
    color: var(--v-txtSecondary-base);
}

.day-group {
    flex: 0 0 auto;
}

.day-tab {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 3px;
    min-width: 54px;
    padding: 7px 12px;
    margin-right: 6px;
    border-radius: 12px;
    background: var(--v-surface-base);
    border: 1px solid var(--v-outline-base);
    transition: border-color 0.15s, color 0.15s;
}

.day-name {
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    white-space: nowrap;
    color: var(--v-txtMuted-base);
}

.day-count {
    font-size: 16px;
    font-weight: 800;
    line-height: 1.1;
    color: var(--v-txtPrimary-base);
}

.day-count--none {
    color: var(--v-txtMuted-base);
}

.day-tab--active {
    border-color: var(--v-primary-base);
}

.day-tab--active .day-name,
.day-tab--active .day-count {
    color: var(--v-primary-base);
}

.shift-list {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.shift-row {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 14px 16px;
    border-radius: 14px;
    background: var(--v-surface-base);
    border: 1px solid var(--v-outline-base);
}

.shift-dot {
    width: 10px;
    height: 10px;
    flex-shrink: 0;
    border-radius: 50%;
}

.shift-info {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    min-width: 0;
}

.shift-name {
    font-size: var(--fs-body);
    font-weight: 700;
    color: var(--v-txtPrimary-base);
}

.shift-time {
    margin-top: 2px;
    font-size: var(--fs-caption);
    color: var(--v-txtMuted-base);
}

.shift-seats {
    margin-top: 4px;
    font-size: 11px;
    font-weight: 600;
    color: var(--v-txtMuted-base);
}

.shift-seats--full {
    color: var(--v-error-base);
}

.shift-row--blocked {
    opacity: 0.6;
}

.shift-action {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 5px;
    flex-shrink: 0;
}

.shift-chip {
    padding: 3px 10px;
    border-radius: 999px;
    background: var(--v-surface2-base);
    border: 1px solid var(--v-outline-base);
    font-size: var(--fs-caption);
    font-weight: 700;
    color: var(--v-txtSecondary-base);
}

.shift-chip--fixed {
    background: rgba(0, 153, 102, 0.12);
    border-color: rgba(0, 153, 102, 0.4);
    color: var(--v-success-base);
}

.shift-cancel {
    font-size: 11px;
    font-weight: 600;
    color: var(--v-error-base);
}

.shift-cancel:hover:not(:disabled) {
    text-decoration: underline;
}

.shift-cancel:disabled {
    opacity: 0.5;
}

.book-empty {
    padding: 40px 20px;
    text-align: center;
    font-size: var(--fs-caption);
    color: var(--v-txtMuted-base);
}

.theme--light .day-tab--active {
    border-color: var(--v-accent-base);
}

.theme--light .day-tab--active .day-name,
.theme--light .day-tab--active .day-count {
    color: var(--v-accent-base);
}

</style>
